import React, { useEffect, useMemo, useRef } from 'react';
import { GenerateIcon, ICONS } from '@pileus-cloud/anodot-frontend-common';
import Checkbox from 'shared/components/andtComponents/Checkbox';
import InfoPopover from 'shared/components/andtComponents/InfoPopover';
import classNames from 'classnames';
import { Search } from '../Search';
import { ENTITIES } from '../../consts';
import { countSelectedLinkedAccounts } from './dataAccessHelperFunctions';
import FullAccessCheckbox from 'users/containers/Organization/Roles/rolesExpandedRowComponents/DataAccess/FullAccessCheckbox';

import styles from './AccountsComponent.module.scss';

const PayerAccountsHeader = ({
  allAccountsData,
  isCreateEditMode,
  isSelectAll,
  isViewOnly,
  modifiedSelectionById,
  onSelectAll,
  onFullAccessSelectChanged,
  search,
  setSearch,
  entity,
}) => {
  const fullAccessData = allAccountsData?.fullAccessData;

  const countOfAllLinkedAccounts = useMemo(() => {
    let totalLinkedAccounts = 0;
    if (Array.isArray(allAccountsData?.accountsData)) {
      allAccountsData.accountsData?.forEach((account) => {
        totalLinkedAccounts += account?.linkedAccounts?.length || 0;
      });
    }
    return totalLinkedAccounts;
  }, [allAccountsData]);

  const countOfAllSelectedLinkedAccounts = useMemo(() => {
    let selectedCounter = 0;
    if (fullAccessData?.isFullAccess) {
      selectedCounter = countOfAllLinkedAccounts;
    } else {
      selectedCounter = countSelectedLinkedAccounts(allAccountsData?.accountsData, modifiedSelectionById);
    }
    return selectedCounter;
  }, [allAccountsData?.accountsData, countOfAllLinkedAccounts, fullAccessData?.isFullAccess, modifiedSelectionById]);

  const counterRef = useRef(null);

  useEffect(() => {
    if (isCreateEditMode && counterRef?.current) {
      // Calculate the max possible width of the counter element, so the Select All will not move when on counter change
      const tempDiv = document.createElement('div');
      tempDiv.style.visibility = 'hidden';
      tempDiv.style.position = 'absolute';
      tempDiv.style.whiteSpace = 'nowrap';

      const counterStyles = window.getComputedStyle(counterRef.current);
      tempDiv.style.font = counterStyles.font;

      tempDiv.textContent = `${countOfAllLinkedAccounts}/${countOfAllLinkedAccounts}`;
      document.body.appendChild(tempDiv);

      const maxWidth = tempDiv.offsetWidth;
      counterRef.current.style.minWidth = `${maxWidth + 2}px`;

      document.body.removeChild(tempDiv);
    }
  }, [countOfAllLinkedAccounts, isCreateEditMode]);

  const SelectAllContent = () => (
    <div className={styles.selectAll}>
      <div>Select All</div>
      <InfoPopover className={{ icon: classNames('d-flex', 'align-items-center') }} placement="top">
        {`Note that accounts assigned\n from other ${
          Object.values(ENTITIES).find((e) => e.id === entity).label
        }s will not be\n ${isCreateEditMode ? 'added' : 'removed'}`}
      </InfoPopover>
    </div>
  );

  return (
    <div className={styles.headerContainer}>
      <div className={styles.headerLeft}>
        {isCreateEditMode ? (
          <>
            <div className={styles.headerTitle}>
              Selected Total
              <div className={styles.headerCounters} ref={counterRef}>
                <span className={styles.selectedCounter}>{countOfAllSelectedLinkedAccounts}</span>/
                <span>{countOfAllLinkedAccounts}</span>
              </div>
            </div>
            <Checkbox
              isChecked={isSelectAll}
              isDisabled={isViewOnly || fullAccessData?.isFullAccess}
              onChange={() => onSelectAll(!isSelectAll)}
              primary
              isSimple
              text={SelectAllContent()}
            />
          </>
        ) : null}
        {entity === ENTITIES.ROLE.id && (fullAccessData?.isFullAccess || isCreateEditMode) ? (
          <FullAccessCheckbox
            isDisabled={isViewOnly || !isCreateEditMode || fullAccessData?.isFullAccessInherited}
            isFullAccess={fullAccessData?.isFullAccess}
            onFullAccessSelectChanged={onFullAccessSelectChanged}
          />
        ) : null}
        {isCreateEditMode ? null : (
          <div className={styles.viewModeText}>
            <GenerateIcon iconName={ICONS.circleInfo.name} className={styles.infoIcon} />
            <div>{`To ${
              fullAccessData?.isFullAccess ? 'remove full access' : ' add and remove accounts'
            }, click the Add / Remove Accounts button`}</div>
            <div className={styles.dummyButton}>
              <GenerateIcon iconName={ICONS.addLinkedAccounts.name} className={styles.dummyButtonIcon} />
            </div>
          </div>
        )}
      </div>
      <div className={classNames(styles.headerRight, { [styles.createEdit]: isCreateEditMode })}>
        <Search search={search} setSearch={setSearch} />
      </div>
    </div>
  );
};


export default PayerAccountsHeader;
